import React, { Fragment } from "react";
import { map, isEmpty, find, filter, groupBy, head } from "lodash";
import InfoCard from "app/main/applicationWorklist/components/info-card";
import { Typography } from "@material-ui/core";
import DefaultButton from "components/items/default-button";
import { getIPACollapseRestrictionHtml } from "utils/get-environment-variables";

const RestrictionDisplay = props => {
  const { drugResultJson, strengthName, formularies, showOffFormularyResults, showStrengthRestrictionOnForm } = props;
  const json = JSON.parse(drugResultJson);
  const strengthSelected = !isEmpty(strengthName);

  const getRestrictions = () => {
    const strength = find(json.strengths, x => x.name === strengthName);
    const hasStrength = !isEmpty(strength);
    return filter(json.restrictions, restriction => restriction.strengthId === (hasStrength ? strength.strengthId : null));
  };

  const strengthRestrictions = getRestrictions();

  const restrictions = groupBy(strengthRestrictions, x => x.formularyId);
  if (!showOffFormularyResults) {
    if (strengthSelected && isEmpty(restrictions)) { return null; }
    if (!strengthSelected && isEmpty(json.restrictions)) { return null; }
  }

  const getRestrictionSeverity = restrictionLevelType => {
    switch (restrictionLevelType) {
      case "Green": return "success";
      case "Amber": return "warning";
      case "Red": return "error";
      case "Stop": return "error";
      case "Grey": return "error";
      case "Black": return "error";
      default: return null;
    }
  };

  const getRestrictionIcon = restrictionLevelType => {
    switch (restrictionLevelType) {
      case "Green": return "check_circle";
      case "Amber": return "info";
      case "Red": return "error_outline";
      case "Stop": return "error_outline";
      case "Grey": return "cancel";
      case "Black": return "cancel";
      default: return "check_circle";
    }
  };

  const getRestrictionSeverityFromDescription = restrictionLevel => {
    switch (restrictionLevel) {
      case "Restricted": return "warning";
      case "Unrestricted": return "success";
      case "Non-Formulary": return "error";
      case "Highly Restricted": return "error";
      default: return "warning";
    }
  };

  const indicationOutput = indication => {
    // taken from formulary indication-pill-box css style
    const offLabelStyle = {
      background: "#D7D7D7",
      borderRadius: "45px",
      padding: "2px 4px",
      fontSize: 9,
      whiteSpace: "nowrap",
      alignItems: "center",
      display: "flex",
    };

    // if it's off label, then it is italicized with (off-label) after it
    if (indication.offLabel) {
      return <><i>{indication.name}</i>&nbsp;<span style={offLabelStyle}>off-label</span></>;
    }

    // if it's not off label, then it is just the name and bolded
    return <b>{indication.name}</b>;
  };

  const getRestrictionTitleWithIndications = restriction => {
    // Ensures the title and indications are on the same line
    const rowStyle = {
      display: "flex",
      flexDirection: "row",
    };

    const title = (
      <Typography>
        {restriction?.restrictionLevelDescription ?? restriction?.restrictionLevelType}
      </Typography>
    );

    let indications;

    if (!isEmpty(restriction.indications)) {
      indications = (
        <span style={{ ...rowStyle, fontWeight: 400 }}>
          &nbsp;for:
          {map(restriction.indications, (indication, index) => (
            <Typography key={index} style={rowStyle}>
              &nbsp;
              {indicationOutput(indication)}
              {`${index < restriction.indications.length - 1 ? ", " : ""}`}
            </Typography>
          ))}
        </span>
      );
    }

    return (
      <span style={rowStyle}>
        {title}
        {indications}
      </span>
    );
  };

  const getOffFormularyMessageTitle = offFormularyMessage => {
    const titleText = offFormularyMessage?.level ?? "Not On Formulary";

    return (
      <span><Typography>{titleText}</Typography></span>
    );
  };

  const getHeadlineForRestriction = restriction => {
    let response = null;

    if (restriction.appliesToTradeProductName) {
      response = `Restriction on brand product: ${restriction.appliesToTradeProductName}`;
    }

    return response;
  };

  const selectedOnFormulary = formularyId => {
    let { onFormularies } = json;
    const strength = find(json.strengths, x => x.name === strengthName);

    if (strength) {
      onFormularies = strength.onFormularies;
    }

    const onFormularyEntry = find(onFormularies, x => x.id === formularyId);
    if (onFormularyEntry) {
      return true;
    }

    const strengthsOnFormulary = find(json.strengths, x => find(x.formularyIds, id => id === formularyId));
    if (strengthsOnFormulary) {
      return true;
    }

    return false;
  };

  const getOffFormularyMessage = formularyId => {
    const { offFormularyMessages } = json;

    if (isEmpty(offFormularyMessages)) {
      return null;
    }

    const { drugId, formId } = json;

    const strength = find(json.strengths, x => x.name === strengthName);

    if (strength) {
      const strengthOffFormularyMessage = find(offFormularyMessages, x => x.strengthId === strength.strengthId && x.formularyId === formularyId);

      if (!isEmpty(strengthOffFormularyMessage)) {
        return strengthOffFormularyMessage;
      }
    }

    return find(offFormularyMessages, x => x.strengthId === null && x.drugId === drugId && x.formId === formId && x.formularyId === formularyId);
  };

  const collapseEnabled = getIPACollapseRestrictionHtml();

  const MAX_LINES = 5;
  const lineClampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: MAX_LINES,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  const measureHiddenStyle = {
    position: "fixed",
    top: -10000,
    left: -10000,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "normal",
  };

  const RestrictionHtml = ({ html, collapseFlag }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [canCollapse, setCanCollapse] = React.useState(false);
    const viewRef = React.useRef(null);
    const measureRef = React.useRef(null);

    // Set HTML content
    React.useEffect(() => {
      if (viewRef.current) viewRef.current.innerHTML = html || "";
      if (measureRef.current) {
        measureRef.current.innerHTML = html || "";
        // Match measurement width to visible container for accurate line count
        const width = viewRef.current ? viewRef.current.clientWidth : undefined;
        if (width) {
          measureRef.current.style.width = `${width}px`;
        }
      }
    }, [html]);

    // Measure lines and determine if collapse is needed
    React.useEffect(() => {
      if (!collapseFlag) { setCanCollapse(false); return; }
      // Defer to ensure layout settles
      window.requestAnimationFrame(() => {
        if (!measureRef.current || !viewRef.current) return;
        const computed = window.getComputedStyle(viewRef.current);
        const lineHeight = parseFloat(computed.lineHeight) || 16;
        const lines = Math.round(measureRef.current.scrollHeight / lineHeight);
        setCanCollapse(lines > MAX_LINES);
      });
    }, [html, collapseFlag]);

    const clamped = canCollapse && !expanded;
    return (
      <>
        <div ref={viewRef} style={clamped ? lineClampStyle : undefined} />
        {/* hidden measurement box */}
        <div ref={measureRef} style={measureHiddenStyle} />
        {canCollapse && (
          <div className="mt-8 flex items-center justify-center">
            {/* left and right spacers balance the MuiAlert icon on the left */}
            <span className="inline-block" style={{ width: 24 }} />
            <DefaultButton
              label={expanded ? "See less" : "See more"}
              onClick={() => setExpanded(prev => !prev)}
              size="small"
              icon={expanded ? "expand_less" : "expand_more"}
              variant="contained"
              color="default"
            />
            <span className="inline-block" style={{ width: 24 }} />
          </div>
        )}
      </>
    );
  };

  if (!showStrengthRestrictionOnForm && isEmpty(strengthName)) {
    return (
      <div className="flex justify-center items-center p-16">
        <Typography variant="body1" className="font-bold" style={{ fontStyle: "italic" }}>
          Please select a strength to view restriction details
        </Typography>
      </div>
    );
  }

  return (
    <div className="items-container pl-32 pr-16 w-auto">
      {map(formularies, (formulary, index) => {
        const formularyHeader = `${formulary.formularyName} Restrictions:\n`;
        const isSelectedOnFormulary = selectedOnFormulary(parseInt(formulary.formularyId, 10));

        if (!isSelectedOnFormulary) {
          const offFormularyMessage = getOffFormularyMessage(parseInt(formulary.formularyId, 10));
          const offFormularyText = offFormularyMessage?.message;
          return (
            <Fragment key={index}>
              <Typography variant="body1" className="font-bold">
                {formularyHeader}
              </Typography>
              <InfoCard
                key={1}
                icon={getRestrictionIcon(offFormularyMessage?.levelType ?? "Amber")}
                severity={getRestrictionSeverity(offFormularyMessage?.levelType ?? "Red")}
                title={getOffFormularyMessageTitle(offFormularyMessage)}
              >
                {/* eslint-disable-next-line react/no-danger */}
                {offFormularyText ? <RestrictionHtml html={offFormularyText} collapseFlag={collapseEnabled} /> : null}
              </InfoCard>
            </Fragment>
          );
        }
        // Find the restriction group for the current formulary
        const restrictionGroup = find(restrictions, rGroup => head(rGroup)?.formularyId === parseInt(formulary.formularyId, 10));

        if (isEmpty(restrictionGroup)) {
          return (
            <Fragment key={index}>
              <Typography variant="body1" className="font-bold">
                {formularyHeader}
              </Typography>
              <InfoCard key={1} severity="info">
                <Typography>No Restrictions Applied</Typography>
              </InfoCard>
            </Fragment>
          );
        }

        return (
          <Fragment key={index}>
            <Typography
              variant="body1"
              className="font-bold"
            >
              {formularyHeader}
            </Typography>
            {map(restrictionGroup, (restriction, restrictionIndex) => {
              const html = isEmpty(restriction.stampedRestrictionHtml) ? restriction.customText : restriction.stampedRestrictionHtml;
              return (
                <InfoCard
                  key={restrictionIndex}
                  icon={getRestrictionIcon(restriction?.restrictionLevelType)}
                  severity={getRestrictionSeverity(restriction?.restrictionLevelType)
                    ?? getRestrictionSeverityFromDescription(restriction?.restrictionLevel ?? restriction?.restrictionLevelDescription ?? restriction.restrictionLevelType)}
                  headline={(getHeadlineForRestriction(restriction))}
                  title={getRestrictionTitleWithIndications(restriction)}
                >
                  {html && (
                    <RestrictionHtml html={html} collapseFlag={collapseEnabled} />
                  )}
                </InfoCard>
              );
            })}
          </Fragment>
        );
      })}
    </div>
  );
};

export default RestrictionDisplay;
